import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import {
  UppKonvaGroup,
  UppKonvaModel,
  type UppKonvaAreaSource,
  type UppKonvaAttention,
  type UppKonvaDrawMode,
  type UppKonvaLabelFontSize,
  type UppKonvaOrientation,
  type UppKonvaShapeKind,
  type UppKonvaShapeSource,
  type UppKonvaTheme,
} from '@unpispas/upp-konva';

import { demoResolveAreaShapeById } from './demo-resolve-area-shape-by-id';

/** Instancia viva en {@link UppKonvaModel.area}.`shapes` (no el tipo wire `UppKonvaShape` del adaptador). */
type UppKonvaDemoAreaShape = UppKonvaModel['area']['shapes'][number];

function demoUuid(): string {
  const c = globalThis.crypto as Crypto | undefined;
  return c?.randomUUID?.() ?? `id-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function isAreaSourceJson(v: unknown): v is UppKonvaAreaSource {
  if (!isRecord(v)) {
    return false;
  }
  return (
    typeof v['width'] === 'number' &&
    typeof v['height'] === 'number' &&
    Array.isArray(v['shapes'])
  );
}

/** Entradas de la paleta izquierda: mismos `kind` que {@link UppKonvaShapeKind} (DTO vía modelo, sin stage). */
/** Opciones de `data.shape` en el inspector de mesa (valor enlazado con `ngModel`). */
const INSPECTOR_TABLE_SHAPES = ['SQUARE', 'CIRCLE'] as const;
const INSPECTOR_BAR_SHAPES = ['LINEAR', 'TOLEFT', 'TORIGHT', 'USHAPE'] as const;
const INSPECTOR_DOOR_SHAPES = ['TOLEFT', 'TORIGHT'] as const;
const INSPECTOR_WINDOW_SHAPES = ['SINGLE', 'DOUBLE'] as const;
const INSPECTOR_STAIRS_SHAPES = ['SIMPLE', 'DOUBLE', 'SNAIL'] as const;
const INSPECTOR_SOFA_SHAPES = ['LINEAR', 'TOLEFT', 'TORIGHT'] as const;
const INSPECTOR_GENERIC_SHAPES = ['SQUARE', 'ROUND'] as const;

/**
 * Activa trazas de depuración si:
 * - La URL lleva `konvaDemoDebug=1` en **`search` o en `hash`** (con HashRouter, `?x=1` suele ir **dentro** del hash, p. ej. `#/demo/konva?konvaDemoDebug=1`; entonces `location.search` está vacío).
 * - O `localStorage.setItem('konvaDemoDebug','1')` y recargas.
 */
function isKonvaDemoDebugEnabled(): boolean {
  try {
    const loc = globalThis.location;
    if (!loc) {
      return false;
    }
    const bundle = `${loc.search ?? ''}\n${loc.hash ?? ''}`;
    if (/[?&]konvaDemoDebug=1(?:&|$|#)/.test(bundle)) {
      return true;
    }
    return globalThis.localStorage?.getItem('konvaDemoDebug') === '1';
  } catch {
    return false;
  }
}

const PALETTE_ENTRIES: readonly Readonly<{
  kind: UppKonvaShapeKind;
  label: string;
  sym: string;
  dataId?: string;
}>[] = [
  { kind: 'TABLE', label: 'Mesa', sym: '🪑', dataId: 'konva-demo-add-table' },
  { kind: 'BAR', label: 'Barra', sym: '🍸' },
  { kind: 'SOFA', label: 'Sofá', sym: '🛋️' },
  { kind: 'DOOR', label: 'Puerta', sym: '🚪' },
  { kind: 'WINDOW', label: 'Ventana', sym: '🪟' },
  { kind: 'STAIRS', label: 'Escaleras', sym: '🪜' },
  { kind: 'BILLIARD', label: 'Billar', sym: '🎱' },
  { kind: 'FOOTBALL', label: 'Futbolín', sym: '⚽' },
  { kind: 'GENERIC', label: 'Genérico', sym: '▢', dataId: 'konva-demo-add-generic' },
  { kind: 'VERTEX', label: 'Vértice', sym: '◇' },
];

@Component({
  selector: 'upp-konva-demo',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './demo-konva.component.html',
  styleUrls: ['./demo-konva.component.scss'],
})
export class DemoKonvaComponent implements OnInit, OnDestroy {
  /** Única superficie soportada por la demo: API de {@link UppKonvaModel} + host `upp-konva` (stub visual). */
  model!: UppKonvaModel;

  readonly paletteEntries = PALETTE_ENTRIES;

  readonly inspectorTableShapes = INSPECTOR_TABLE_SHAPES;
  readonly inspectorBarShapes = INSPECTOR_BAR_SHAPES;
  readonly inspectorDoorShapes = INSPECTOR_DOOR_SHAPES;
  readonly inspectorWindowShapes = INSPECTOR_WINDOW_SHAPES;
  readonly inspectorStairsShapes = INSPECTOR_STAIRS_SHAPES;
  readonly inspectorSofaShapes = INSPECTOR_SOFA_SHAPES;
  readonly inspectorGenericShapes = INSPECTOR_GENERIC_SHAPES;

  useDarkTheme = false;
  sceneMode: 'view' | 'edit' = 'edit';
  importError: string | null = null;

  /** Borradores (cm) para el panel Estado; se aplican a la sala con {@link applyRoomDimensions}. */
  roomWidthCm = 800;
  roomHeightCm = 600;

  /** Última selección del lienzo (`onSelect` de `upp-konva`): instancias de {@link UppKonvaModel.area}.`shapes`. */
  canvasSelection: readonly UppKonvaDemoAreaShape[] = [];

  /** Figura del modelo alineada con una selección única; `null` si no hay una sola fila coincidente. */
  inspectorShape: UppKonvaDemoAreaShape | null = null;

  /**
   * Selección múltiple homogénea (mismo `kind`, todas resueltas en `area.shapes`). Vacío si no aplica.
   * No incluye el caso solo-vértices (sin panel de geometría común en esta demo).
   */
  inspectorMultiShapes: UppKonvaDemoAreaShape[] = [];

  /** `kind` común cuando {@link inspectorMultiShapes} tiene al menos dos filas. */
  inspectorMultiUniformKind: UppKonvaShapeKind | null = null;

  /** Borradores para aplicar geometría uniforme vía stage (multi-selección). */
  multiGeomWidthCm = 0;
  multiGeomHeightCm = 0;
  multiGeomRotationDeg = 0;

  /** Sillas (multi mesas): valor mostrado; se aplica a todas las mesas seleccionadas. */
  multiInspectorChairs = 4;

  /**
   * Último grupo de multi-selección emitido por {@link UppKonvaModel.area}.`onGrouped$` (alinear / distribuir / geometría uniforme).
   */
  canvasGroup: UppKonvaGroup | null = null;

  private _onGroupedSub: Subscription | null = null;

  /**
   * Copias explícitas para cada `<select>` de variante de forma: `ngModel` con getter (`inspectorDataString`)
   * desincroniza el control interno de Angular en la **primera** interacción (OnPush + valor recalculado).
   */
  inspectorTableShapeControl: (typeof INSPECTOR_TABLE_SHAPES)[number] = 'SQUARE';
  inspectorBarShapeControl: (typeof INSPECTOR_BAR_SHAPES)[number] = 'LINEAR';
  inspectorDoorShapeControl: (typeof INSPECTOR_DOOR_SHAPES)[number] = 'TOLEFT';
  inspectorWindowShapeControl: (typeof INSPECTOR_WINDOW_SHAPES)[number] = 'SINGLE';
  inspectorStairsShapeControl: (typeof INSPECTOR_STAIRS_SHAPES)[number] = 'SIMPLE';
  inspectorSofaShapeControl: (typeof INSPECTOR_SOFA_SHAPES)[number] = 'LINEAR';
  inspectorGenericShapeControl: (typeof INSPECTOR_GENERIC_SHAPES)[number] = 'SQUARE';

  /** Ver {@link isKonvaDemoDebugEnabled}: trazas `[konva-demo]` y `globalThis.__UPP_KONVA_TRACE__` en la librería. */
  private _demoTraceOn = false;

  constructor(private readonly _cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this._demoTraceOn = isKonvaDemoDebugEnabled();
    if (this._demoTraceOn) {
      (globalThis as { __UPP_KONVA_TRACE__?: boolean }).__UPP_KONVA_TRACE__ = true;
      console.warn(
        '[konva-demo] Debug ON — trazas con console.log. Activar con: (1) `#/ruta?konvaDemoDebug=1` o `?konvaDemoDebug=1` antes del #, (2) localStorage konvaDemoDebug=1',
      );
    }
    this.model = new UppKonvaModel(this._initialSource());
    this.model.theme = this.themePreset;
    this._syncRoomSizeInputsFromModel();
    this._onGroupedSub = this.model.area.onGrouped$.subscribe((g: UppKonvaGroup | null) => {
      this.canvasGroup = g;
      this._cdr.markForCheck();
    });
    /** `sceneMode` ya es `edit` por defecto; sin esto el modelo quedaría en VIEW y el stage no aceptaría arrastre. */
    this.onSceneModeChange();
  }

  ngOnDestroy(): void {
    this._onGroupedSub?.unsubscribe();
    this._onGroupedSub = null;
  }

  get themePreset(): UppKonvaTheme {
    return this.useDarkTheme ? 'UPPKONVA_DARKTHEME' : 'UPPKONVA_LIGHTTHEME';
  }

  onSceneModeChange(): void {
    this.model.mode = this.sceneMode === 'edit' ? 'EDIT' : 'VIEW';
    this._cdr.markForCheck();
  }

  setSceneMode(mode: 'view' | 'edit'): void {
    this.sceneMode = mode;
    this.onSceneModeChange();
  }

  toggleTheme(): void {
    this.useDarkTheme = !this.useDarkTheme;
    this.model.theme = this.themePreset;
    this._cdr.markForCheck();
  }

  onModelStructuralChange(): void {
    this._demoTrace('onModelStructuralChange (onChange del host)');
    this.importError = null;
    this._syncRoomSizeInputsFromModel();
    this._syncInspectorAfterStructuralChange();
    if (this.canvasSelection.length > 1) {
      this._resolveInspectorShape();
    }
    this._cdr.markForCheck();
  }

  /**
   * Aplica {@link roomWidthCm} y {@link roomHeightCm} al área vía {@link UppKonvaModel.applyAreaFromSource}
   * (conserva figuras y metadatos).
   */
  applyRoomDimensions(): void {
    const w = Math.trunc(Number(this.roomWidthCm));
    const h = Math.trunc(Number(this.roomHeightCm));
    if (!Number.isFinite(w) || !Number.isFinite(h) || w < 1 || h < 1) {
      this._syncRoomSizeInputsFromModel();
      this._cdr.markForCheck();
      return;
    }
    const src = this.model.area.source;
    this.model.applyAreaFromSource({
      ...src,
      width: w,
      height: h,
    });
    this.roomWidthCm = w;
    this.roomHeightCm = h;
    this._cdr.markForCheck();
  }

  onCanvasSelectionChange(sel: readonly UppKonvaDemoAreaShape[]): void {
    this._demoTrace('onCanvasSelectionChange', { count: sel.length, ids: sel.map((s) => s.id) });
    this.canvasSelection = sel;
    this._resolveInspectorShape();
    this._cdr.markForCheck();
  }

  /** Id de fila del elemento seleccionado (coincide con el UUID canónico del lienzo). */
  inspectorSceneUuid(): string | null {
    return this.canvasSelection.length === 1 ? (this.canvasSelection[0]?.id ?? null) : null;
  }

  inspectorAttentionButtonLabel(): string {
    const id = this.inspectorSceneUuid();
    if (!id) {
      return 'Resaltar';
    }
    const shape = this.model.area.findShapeById(id);
    if (!shape || shape.kind !== 'TABLE') {
      return 'Resaltar (solo mesa)';
    }
    return shape.attention ? 'Quitar resaltado' : 'Resaltar (WARNING + pulso)';
  }

  /** En vista, el resaltado con API pública solo aplica a filas TABLE. */
  inspectorViewHighlightEnabled(): boolean {
    return this.inspectorShape?.kind === 'TABLE';
  }

  inspectorHighlightSummaryText(): string {
    const id = this.inspectorSceneUuid();
    if (!id) {
      return '—';
    }
    const shape = this.model.area.findShapeById(id);
    if (!shape || shape.kind !== 'TABLE') {
      return '—';
    }
    const h = shape.highlight;
    if (!h) {
      return 'No';
    }
    return `${h.tone} · ${h.animated ? 'pulso' : 'solo color'}`;
  }

  setInspectorViewHighlight(tone: UppKonvaAttention, animated: boolean): void {
    const id = this.inspectorSceneUuid();
    if (!id) {
      return;
    }
    const shape = this.model.area.findShapeById(id);
    if (!shape || shape.kind !== 'TABLE') {
      return;
    }
    shape.setHighlight(tone, animated);
    this._cdr.markForCheck();
  }

  clearInspectorViewHighlight(): void {
    const id = this.inspectorSceneUuid();
    if (!id) {
      return;
    }
    this.model.area.findShapeById(id)?.clearHighlight();
    this._cdr.markForCheck();
  }

  inspectorAttentionToggleDisabled(): boolean {
    return !this.inspectorViewHighlightEnabled();
  }

  /** `true` si el panel multi homogéneo puede mostrarse (≥ 2 figuras, mismo kind, todas en el modelo). */
  inspectorMultiUniformReady(): boolean {
    return this.inspectorMultiShapes.length >= 2 && this.inspectorMultiUniformKind != null;
  }

  onMultiAlign(alignment: 'vertical' | 'horizontal'): void {
    const axis = alignment === 'vertical' ? 'VERTICAL' : 'HORIZONTAL';
    this.canvasGroup?.align(axis);
  }

  onMultiDistribute(axis: 'vertical' | 'horizontal'): void {
    const konvaAxis = axis === 'vertical' ? 'VERTICAL' : 'HORIZONTAL';
    this.canvasGroup?.distribute(konvaAxis);
  }

  onMultiGeometryChange(): void {
    const applied = this.canvasGroup?.applyUniformGeometry(
      this.multiGeomWidthCm,
      this.multiGeomHeightCm,
      this.multiGeomRotationDeg,
    );
    if (applied) {
      this._resolveInspectorShape();
    }
    this._cdr.markForCheck();
  }

  onMultiInspectorTableShapeControlChange(v: unknown): void {
    if (this.inspectorMultiUniformKind !== 'TABLE' || (v !== 'CIRCLE' && v !== 'SQUARE')) {
      return;
    }
    this.inspectorTableShapeControl = v;
    for (const s of this.inspectorMultiShapes) {
      if (s.kind === 'TABLE') {
        s.data['shape'] = v;
        s.syncPresentationToStage();
      }
    }
    this.model.area.notifyPresentationDirty();
    this.model.doRefresh();
    this._resolveInspectorShape();
    this._cdr.markForCheck();
  }

  onMultiChairsChange(value: number): void {
    const n = Number(value);
    this.multiInspectorChairs = Number.isFinite(n) ? n : 0;
    for (const s of this.inspectorMultiShapes) {
      if (s.kind === 'TABLE') {
        s.data['chairs'] = this.multiInspectorChairs;
        s.syncPresentationToStage();
      }
    }
    this.model.area.notifyPresentationDirty();
    this.model.doRefresh();
    this._resolveInspectorShape();
    this._cdr.markForCheck();
  }

  toggleInspectorAttention(): void {
    const id = this.inspectorSceneUuid();
    if (!id) {
      return;
    }
    const shape = this.model.area.findShapeById(id);
    if (!shape || shape.kind !== 'TABLE') {
      return;
    }
    if (shape.attention) {
      shape.clearHighlight();
    } else {
      shape.setHighlight('WARNING', true);
    }
    this._cdr.markForCheck();
  }

  /**
   * Campos x/y/ancho/alto/rotación: el lienzo es canónico hasta que se empuja el área pública al stage.
   */
  onInspectorGeometryChange(): void {
    this._demoTrace('onInspectorGeometryChange', this._inspectorDebugSnapshot());
    this.model.area.notifyPresentationDirty();
    this.model.doRefresh();
    this._syncInspectorAfterStructuralChange();
    this._cdr.markForCheck();
  }

  /** Etiqueta / subetiqueta: {@link UppKonvaShape.syncLabelsToStage} repinta el badge en Konva (no basta `notifyPresentationDirty` + `doRefresh`). */
  onInspectorLabelPresentationChange(): void {
    this.inspectorShape?.syncLabelsToStage();
    this._cdr.markForCheck();
  }

  /**
   * Variante visual y `data` (forma, sillas, taburetes, …): **`shape.syncPresentationToStage()`**.
   * sin pisar geometría interna; otros tipos → repintado desde el área pública.
   */
  onInspectorPresentationChange(): void {
    this._demoTrace('onInspectorPresentationChange:before', this._inspectorDebugSnapshot());
    const s = this.inspectorShape;
    const syncPresentationKinds = new Set<UppKonvaShapeKind>([
      'TABLE',
      'BAR',
      'DOOR',
      'WINDOW',
      'STAIRS',
      'SOFA',
      'GENERIC',
    ]);
    if (s && syncPresentationKinds.has(s.kind)) {
      s.syncPresentationToStage();
    } else {
      this.model.area.notifyPresentationDirty();
      this.model.doRefresh();
    }
    this._syncInspectorAfterStructuralChange();
    this._demoTrace('onInspectorPresentationChange:after', this._inspectorDebugSnapshot());
    this._cdr.markForCheck();
  }

  inspectorDataString(key: string, fallback: string): string {
    const s = this.inspectorShape;
    if (!s) {
      return fallback;
    }
    const v = s.data[key];
    return typeof v === 'string' && v.length > 0 ? v : fallback;
  }

  setInspectorDataString(key: string, value: string): void {
    if (!this.inspectorShape) {
      return;
    }
    this.inspectorShape.data[key] = value;
    this.onInspectorPresentationChange();
  }

  /** Valor del `<select>` de forma de mesa (CIRCLE redonda / SQUARE cuadrada en JSON `data`). */
  inspectorTableShapeUi(): 'CIRCLE' | 'SQUARE' {
    const s = this.inspectorShape;
    if (!s) {
      return 'SQUARE';
    }
    return this._tableShapeUiFromData(s.data);
  }

  private _tableShapeUiFromData(data: Record<string, unknown>): 'CIRCLE' | 'SQUARE' {
    const raw = data['shape'];
    const u = typeof raw === 'string' ? raw.toUpperCase() : 'SQUARE';
    return u === 'CIRCLE' ? 'CIRCLE' : 'SQUARE';
  }

  onInspectorTableShapeControlChange(v: unknown): void {
    this._demoTrace('onInspectorTableShapeControlChange', {
      v,
      hasInspector: !!this.inspectorShape,
      kind: this.inspectorShape?.kind,
    });
    if (!this.inspectorShape || this.inspectorShape.kind !== 'TABLE') {
      this._demoTrace('onInspectorTableShapeControlChange: early exit (no TABLE)');
      return;
    }
    if (v !== 'CIRCLE' && v !== 'SQUARE') {
      this._demoTrace('onInspectorTableShapeControlChange: early exit (bad v)');
      return;
    }
    this.inspectorTableShapeControl = v;
    this.inspectorShape.data['shape'] = v;
    this.onInspectorPresentationChange();
  }

  onInspectorBarShapeControlChange(v: unknown): void {
    if (!this.inspectorShape || this.inspectorShape.kind !== 'BAR') {
      return;
    }
    if (!this._isOneOf(v, INSPECTOR_BAR_SHAPES)) {
      return;
    }
    this.inspectorBarShapeControl = v;
    this.inspectorShape.data['shape'] = v;
    this.onInspectorPresentationChange();
  }

  onInspectorDoorShapeControlChange(v: unknown): void {
    if (!this.inspectorShape || this.inspectorShape.kind !== 'DOOR') {
      return;
    }
    if (!this._isOneOf(v, INSPECTOR_DOOR_SHAPES)) {
      return;
    }
    this.inspectorDoorShapeControl = v;
    this.inspectorShape.data['shape'] = v;
    this.onInspectorPresentationChange();
  }

  onInspectorWindowShapeControlChange(v: unknown): void {
    if (!this.inspectorShape || this.inspectorShape.kind !== 'WINDOW') {
      return;
    }
    if (!this._isOneOf(v, INSPECTOR_WINDOW_SHAPES)) {
      return;
    }
    this.inspectorWindowShapeControl = v;
    this.inspectorShape.data['shape'] = v;
    this.onInspectorPresentationChange();
  }

  onInspectorStairsShapeControlChange(v: unknown): void {
    if (!this.inspectorShape || this.inspectorShape.kind !== 'STAIRS') {
      return;
    }
    if (!this._isOneOf(v, INSPECTOR_STAIRS_SHAPES)) {
      return;
    }
    this.inspectorStairsShapeControl = v;
    this.inspectorShape.data['shape'] = v;
    this.onInspectorPresentationChange();
  }

  onInspectorSofaShapeControlChange(v: unknown): void {
    if (!this.inspectorShape || this.inspectorShape.kind !== 'SOFA') {
      return;
    }
    if (!this._isOneOf(v, INSPECTOR_SOFA_SHAPES)) {
      return;
    }
    this.inspectorSofaShapeControl = v;
    this.inspectorShape.data['shape'] = v;
    this.onInspectorPresentationChange();
  }

  onInspectorGenericShapeControlChange(v: unknown): void {
    if (!this.inspectorShape || this.inspectorShape.kind !== 'GENERIC') {
      return;
    }
    if (!this._isOneOf(v, INSPECTOR_GENERIC_SHAPES)) {
      return;
    }
    this.inspectorGenericShapeControl = v;
    this.inspectorShape.data['shape'] = v;
    this.onInspectorPresentationChange();
  }

  inspectorDataNumber(key: string, fallback: number): number {
    const s = this.inspectorShape;
    if (!s) {
      return fallback;
    }
    const v = s.data[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
  }

  setInspectorDataNumber(key: string, value: number): void {
    if (!this.inspectorShape) {
      return;
    }
    const n = Number(value);
    this.inspectorShape.data[key] = Number.isFinite(n) ? n : 0;
    this.onInspectorPresentationChange();
  }

  inspectorDataBool(key: string): boolean {
    return this.inspectorShape?.data[key] === true;
  }

  setInspectorDataBool(key: string, value: boolean): void {
    if (!this.inspectorShape) {
      return;
    }
    if (value) {
      this.inspectorShape.data[key] = true;
    } else {
      delete this.inspectorShape.data[key];
    }
    this.onInspectorPresentationChange();
  }

  inspectorShapeDataPreview(): string {
    const s = this.inspectorShape;
    if (!s) {
      return '—';
    }
    const keys = Object.keys(s.data);
    return keys.length === 0 ? '—' : JSON.stringify(s.data);
  }

  setOrientation(o: UppKonvaOrientation): void {
    const src = this.model.area.source;
    this.model.applyAreaFromSource({ ...src, orientation: o });
    this._cdr.markForCheck();
  }

  setAreaDrawMode(m: UppKonvaDrawMode): void {
    const src = this.model.area.source;
    this.model.applyAreaFromSource({ ...src, mode: m });
    this._cdr.markForCheck();
  }

  setLabelFontSize(size: UppKonvaLabelFontSize): void {
    const src = this.model.area.source;
    this.model.applyAreaFromSource({ ...src, labelSize: size });
    this._cdr.markForCheck();
  }

  /** Añade una figura al documento solo con {@link UppKonvaModel.applyAreaFromSource} (API de modelo). */
  addShapeFromPalette(kind: UppKonvaShapeKind): void {
    const src = this.model.area.source;
    const slot = this._paletteSlot(src.shapes.length);
    const row = this._shapeSourceForKind(kind, slot.x, slot.y);
    this.model.applyAreaFromSource({
      ...src,
      shapes: [...src.shapes, row],
    });
    this._cdr.markForCheck();
  }

  removeLastShape(): void {
    const src = this.model.area.source;
    if (src.shapes.length === 0) {
      return;
    }
    this.model.applyAreaFromSource({
      ...src,
      shapes: src.shapes.slice(0, -1),
    });
    this._cdr.markForCheck();
  }

  deleteInspectorSingleShape(): void {
    if (!this.inspectorShape) {
      return;
    }
    void this.inspectorShape.remove();
    this._cdr.markForCheck();
  }

  deleteInspectorMultiShapes(): void {
    const shapes = [...this.inspectorMultiShapes];
    if (shapes.length < 2) {
      return;
    }
    const g = this.canvasGroup;
    const sel = g?.selection ?? [];
    if (g && sel.length === shapes.length && shapes.every((s) => sel.includes(s))) {
      g.remove();
    } else {
      this.model.area.removeShapes(shapes);
    }
    this._cdr.markForCheck();
  }

  /** Multi-selección en edición sin panel uniforme: borra las figuras vivas seleccionadas. */
  deleteHeterogeneousCanvasSelection(): void {
    const area = this.model.area;
    const toRemove = this.canvasSelection.filter((s) => area.shapes.includes(s));
    area.removeShapes(toRemove);
    this._cdr.markForCheck();
  }

  exportJson(): void {
    const json = JSON.stringify(this.model.area.source, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `upp-konva-area-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    this.importError = null;
    this._cdr.markForCheck();
  }

  onImportFile(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    void file.text().then((text) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(text) as unknown;
      } catch {
        this.importError = 'JSON inválido';
        this._cdr.markForCheck();
        return;
      }
      if (!isAreaSourceJson(parsed)) {
        this.importError = 'Se espera un objeto con width, height (number) y shapes (array).';
        this._cdr.markForCheck();
        return;
      }
      this.model.applyAreaFromSource(parsed);
      this._syncRoomSizeInputsFromModel();
      this.sceneMode = this.model.mode === 'EDIT' ? 'edit' : 'view';
      this.importError = null;
      this._cdr.markForCheck();
    });
  }

  stubExportModel(): string {
    return this.model.exportModel();
  }

  private _paletteSlot(index: number): { x: number; y: number } {
    const col = index % 4;
    const row = Math.floor(index / 4);
    return { x: 32 + col * 96, y: 28 + row * 68 };
  }

  private _shapeSourceForKind(kind: UppKonvaShapeKind, x: number, y: number): UppKonvaShapeSource {
    const id = demoUuid();
    const rotation = 0;
    switch (kind) {
      case 'TABLE':
        return {
          id,
          kind,
          x,
          y,
          width: 90,
          height: 90,
          rotation,
          data: { shape: 'SQUARE', chairs: 4 },
        };
      case 'BAR':
        return {
          id,
          kind,
          x,
          y,
          width: 168,
          height: 52,
          rotation,
          data: { shape: 'LINEAR' },
        };
      case 'SOFA':
        return {
          id,
          kind,
          x,
          y,
          width: 132,
          height: 56,
          rotation,
          data: { shape: 'LINEAR' },
        };
      case 'DOOR':
        return {
          id,
          kind,
          x,
          y,
          width: 72,
          height: 14,
          rotation,
          data: { shape: 'TOLEFT' },
        };
      case 'WINDOW':
        return {
          id,
          kind,
          x,
          y,
          width: 88,
          height: 12,
          rotation,
          data: { shape: 'SINGLE' },
        };
      case 'STAIRS':
        return {
          id,
          kind,
          x,
          y,
          width: 96,
          height: 44,
          rotation,
          data: { shape: 'SIMPLE' },
        };
      case 'BILLIARD':
        return { id, kind, x, y, width: 200, height: 110, rotation };
      case 'FOOTBALL':
        return { id, kind, x, y, width: 176, height: 96, rotation };
      case 'GENERIC':
        return {
          id,
          kind,
          x,
          y,
          width: 64,
          height: 64,
          rotation,
          data: { shape: 'SQUARE' },
        };
      case 'VERTEX':
        return {
          id,
          kind,
          x,
          y,
          width: 1,
          height: 1,
          rotation,
          data: { sort: 0, closed: false, poligon: '' },
        };
      default: {
        const _exhaustive: never = kind;
        return _exhaustive;
      }
    }
  }

  private _initialSource(): UppKonvaAreaSource {
    return {
      width: 800,
      height: 600,
      name: 'Demo (API modelo)',
      orientation: 'AUTO',
      mode: 'SHAPES',
      shapes: [],
    };
  }

  private _syncRoomSizeInputsFromModel(): void {
    this.roomWidthCm = this.model.area.width;
    this.roomHeightCm = this.model.area.height;
  }

  private _resolveInspectorShape(): void {
    this.inspectorShape = null;
    this.inspectorMultiShapes = [];
    this.inspectorMultiUniformKind = null;

    const n = this.canvasSelection.length;
    if (n === 0) {
      this._demoTrace('_resolveInspectorShape: empty selection');
      return;
    }

    if (n === 1) {
      const live = this.canvasSelection[0];
      if (!live) {
        return;
      }
      this.inspectorShape = demoResolveAreaShapeById(this.model.area, live.id) ?? live;
      this._demoTrace('_resolveInspectorShape', {
        id: live.id,
        found: !!this.inspectorShape,
        kind: this.inspectorShape?.kind,
        data: this.inspectorShape ? { ...this.inspectorShape.data } : null,
      });
      this._syncInspectorShapeSelectControlsFromModel();
      return;
    }

    const resolved = this.canvasSelection.map((s) => demoResolveAreaShapeById(this.model.area, s.id) ?? s);
    if (resolved.some((s) => !this.model.area.shapes.includes(s))) {
      this._demoTrace('_resolveInspectorShape: multi, shape not in current area.shapes');
      return;
    }
    const k0 = resolved[0]?.kind;
    if (!k0) {
      return;
    }
    if (!resolved.every((s) => s.kind === k0)) {
      this._demoTrace('_resolveInspectorShape: multi, mixed kinds');
      return;
    }

    this.inspectorMultiShapes = resolved;
    this.inspectorMultiUniformKind = k0;
    this._initMultiGeometryDraftFromFirst();
    this._syncMultiInspectorSelectControlsFromFirst();
    this._demoTrace('_resolveInspectorShape: multi uniform', { kind: k0, count: resolved.length });
  }

  private _initMultiGeometryDraftFromFirst(): void {
    const s = this.inspectorMultiShapes[0];
    if (!s) {
      return;
    }
    this.multiGeomWidthCm = s.width;
    this.multiGeomHeightCm = s.height;
    this.multiGeomRotationDeg = s.rotation;
  }

  private _syncMultiInspectorSelectControlsFromFirst(): void {
    const s = this.inspectorMultiShapes[0];
    if (!s) {
      return;
    }
    this._syncSelectControlsForShape(s);
    if (s.kind === 'TABLE') {
      const chairs = s.data['chairs'];
      this.multiInspectorChairs =
        typeof chairs === 'number' && Number.isFinite(chairs) ? Math.trunc(chairs) : 4;
    }
  }

  private _syncInspectorAfterStructuralChange(): void {
    if (!this.inspectorShape) {
      return;
    }
    const id = this.inspectorShape.id;
    this.inspectorShape = this.model.area.shapes.find((sh: UppKonvaDemoAreaShape) => sh.id === id) ?? null;
    this._syncInspectorShapeSelectControlsFromModel();
  }

  private _syncInspectorShapeSelectControlsFromModel(): void {
    this._syncSelectControlsForShape(this.inspectorShape);
  }

  private _syncSelectControlsForShape(s: UppKonvaDemoAreaShape | null): void {
    if (!s) {
      return;
    }
    switch (s.kind) {
      case 'TABLE':
        this.inspectorTableShapeControl = this._tableShapeUiFromData(s.data);
        break;
      case 'BAR':
        this.inspectorBarShapeControl = this._coerceShapeSelect(s.data['shape'], INSPECTOR_BAR_SHAPES, 'LINEAR');
        break;
      case 'DOOR':
        this.inspectorDoorShapeControl = this._coerceShapeSelect(s.data['shape'], INSPECTOR_DOOR_SHAPES, 'TOLEFT');
        break;
      case 'WINDOW':
        this.inspectorWindowShapeControl = this._coerceShapeSelect(
          s.data['shape'],
          INSPECTOR_WINDOW_SHAPES,
          'SINGLE',
        );
        break;
      case 'STAIRS':
        this.inspectorStairsShapeControl = this._coerceShapeSelect(
          s.data['shape'],
          INSPECTOR_STAIRS_SHAPES,
          'SIMPLE',
        );
        break;
      case 'SOFA':
        this.inspectorSofaShapeControl = this._coerceShapeSelect(s.data['shape'], INSPECTOR_SOFA_SHAPES, 'LINEAR');
        break;
      case 'GENERIC':
        this.inspectorGenericShapeControl = this._coerceShapeSelect(
          s.data['shape'],
          INSPECTOR_GENERIC_SHAPES,
          'SQUARE',
        );
        break;
      default:
        break;
    }
  }

  private _isOneOf<T extends readonly string[]>(v: unknown, allowed: T): v is T[number] {
    return typeof v === 'string' && (allowed as readonly string[]).includes(v);
  }

  private _coerceShapeSelect<T extends readonly string[]>(
    raw: unknown,
    allowed: T,
    fallback: T[number],
  ): T[number] {
    const s = typeof raw === 'string' && raw.length > 0 ? raw : fallback;
    const u = s.toUpperCase();
    const hit = allowed.find((a) => a === s || a.toUpperCase() === u);
    return (hit ?? fallback) as T[number];
  }

  private _demoTrace(msg: string, detail?: unknown): void {
    if (!this._demoTraceOn) {
      return;
    }
    console.log('[konva-demo]', msg, detail !== undefined ? detail : '');
  }

  /** Fila `source.shapes` y `data` vivo del inspector (mismo `id` si existe). */
  private _inspectorDebugSnapshot(): Record<string, unknown> {
    const s = this.inspectorShape;
    if (!s) {
      return { inspector: null };
    }
    const row = this.model.area.source.shapes.find((r: UppKonvaShapeSource) => r.id === s.id);
    const wireData =
      row && 'data' in row && row.data !== null && typeof row.data === 'object' && !Array.isArray(row.data)
        ? { ...(row.data as Record<string, unknown>) }
        : row?.data;
    return {
      id: s.id,
      kind: s.kind,
      data: { ...s.data },
      sourceRowData: wireData,
      tableControl: this.inspectorTableShapeControl,
      sceneEpoch: this.model.presentationEpoch,
    };
  }
}
